import React, { useCallback, useRef } from 'react-x';
import propTypes from 'prop-types-x';
import classNames from 'classnames';
import _debounce from 'lodash/debounce.js';
import { randomNumber } from '../../utilities/randomNumber.js';
import { useEventListener } from '../hooks/useEventListener.js';

export function GalleryGrid(props) {

  const { 
    images, 
    onClickCallback, 
    imageOverlay, 
    responsive, 
    useImageAspectRatio,
    containerClasses,
    blockModifier,
    loading
  } = props;
  
  const block = 'c-gallery-grid';
  const rootElementRef = useRef();
  const imageRefs = useRef([]);
  const DefaultTag = onClickCallback ? 'button' : 'div';

  useEventListener('resize', _debounce(handleResize, 100));

  function handleResize() {
    if (responsive && useImageAspectRatio) {
      imageRefs.current?.forEach(image => {
        setImageHeight(image);
      });
    }
  };

  function imgLoaded(e) {
    if (useImageAspectRatio) {
      setImageHeight(e.target);
    }
    fadeInImage(e.target);
  };

  function setImageHeight(image) {
    const aspectRatio = image.naturalHeight/image.naturalWidth;
    const width = image.width;
    const height = width * aspectRatio;
    const rowEndSpan = Math.round(height/24); // 24 = grid-gap value set in css
    image.parentElement.style.setProperty('--row-end-span', rowEndSpan < 5 ? 5 : rowEndSpan); // min-height of 5
  };

  function fadeInImage(image) {
    setTimeout(() => {
      image.style.setProperty('opacity', 1);
    }, randomNumber(50, 800));
  };

  const gridItem = useCallback((image, index) => {
    const linkUrl = image.href;
    const Tag = linkUrl ? 'a' : DefaultTag;
    const imgSrc = image.base || image.url || image.src;

    return (
      imgSrc ?
        <Tag 
        className={`${block}__image`} 
        key={imgSrc}
        {...( linkUrl && { href: linkUrl, target: '_blank' } )}
        {...( onClickCallback && !linkUrl && { onClick: e => onClickCallback(e.currentTarget.children[0], index) } )}
        >
          <img 
          id={image.id}
          src={imgSrc} 
          alt={image.alt || 'gallery-grid-image'}
          data-image={JSON.stringify(image)}
          ref={el => (imageRefs.current[index] = el)}
          onLoad={imgLoaded} 
          loading={loading} 
          width='100%' 
          height='100%' 
          draggable='false'
          style={{ opacity: 0 }}
          />
          <span className='c-media__image--lazy-placeholder' style={{ opacity: 0.1 }}></span>
          {
            imageOverlay &&
              <span className={`${block}__image-overlay`}>
                { imageOverlay(image, index) }
              </span>
          }
        </Tag>
        : 
        null
    )
  }, []);

  return (
    <section className={classNames({
      [block]: true,
      [`${block}--auto-fit`]: !useImageAspectRatio,
      [`${block}--${blockModifier}`]: blockModifier,
      [containerClasses]: containerClasses
    })} 
    ref={rootElementRef}>
      { 
        images?.map((image, index) => (
          gridItem(image, index)
        ))
      }
    </section>
  )
};

GalleryGrid.propTypes = {
  images: propTypes.array.isRequired,
  onClickCallback: propTypes.func,
  imageOverlay: propTypes.func,
  useImageAspectRatio: propTypes.bool,
  responsive: propTypes.bool,
  loading: propTypes.string
};

GalleryGrid.defaultProps = {
  responsive: true,
  useImageAspectRatio: true,
  loading: 'lazy'
};